import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiAdmin } from "shared/boot/api";
import {
  currentRefreshToken,
  currentToken,
  updateUserRefreshToken,
  updateUserToken,
} from "shared/helpers/authorization";
import { Organisation } from "shared/resources";
import StorageService from "shared/services/StorageService";
import { useUserStore as useSharedUserStore } from "shared/stores/user";
import {
  ACCESS_LEVELS,
  type AccessLevel,
  type AccessLevelsValues,
} from "shared/stores/user/types";
import type { Nullable } from "shared/types";

interface GoogleCredentialResponse {
  clientId: string;
  credential: string;
  select_by: string;
}

declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: {
            auto_select?: boolean;
            callback: (response: GoogleCredentialResponse) => void;
            client_id: string;
          }) => void;
        };
      };
    };
  }
}

const ADMIN_ROLES = {
  ai_admin: "AI Admin",
  api_key_admin: "API Key Admin",
  content_admin: "Content Admin",
  copyright: "Copyright",
  external_der_analyst: "External DER Analyst",
  external_media_analyst: "External Media Analyst",
  limited: "Limited",
  media_analyst: "Media Analyst",
  normal: "Normal",
  overnight_admin: "Overnight Admin",
  sales: "Sales",
  super: "Super",
} as const;

const handleErrors = true;

export const useUserStore = defineStore("adminUser", () => {
  const sharedUserStore = useSharedUserStore();
  const { logout: userLogout } = sharedUserStore;
  const { currentUser } = storeToRefs(sharedUserStore);

  const loginFailed = ref<boolean>(false);
  const loadError = ref<Nullable<unknown>>(null);
  const accessLevels = ref(ACCESS_LEVELS);
  const adminRoles = ref(ADMIN_ROLES);
  const organisation = ref<Organisation>();

  function hasAccessLevel(level: AccessLevel): boolean {
    const currentLevels = currentUser.value.access_level;

    return (ACCESS_LEVELS[level] as ReadonlyArray<AccessLevelsValues>).some(
      (access_level) => currentLevels.includes(access_level)
    );
  }

  const hasSuperAccess = computed(() => hasAccessLevel("super"));

  const hasNormalAccess = computed(() => hasAccessLevel("normal"));

  const hasOrganisationEditAccess = computed(() =>
    hasAccessLevel("organisation_edit")
  );

  const hasOrganisationReadAccess = computed(() =>
    hasAccessLevel("organisation_read")
  );

  const hasAIAccess = computed(() => hasAccessLevel("ai_admin"));

  const hasApiKeyAccess = computed(() => hasAccessLevel("api_key_admin"));

  const hasLimitedAccess = computed(() => hasAccessLevel("limited"));

  const hasRosterAccess = computed(() => hasAccessLevel("overnight_admin"));

  const hasMediaAnalystAccess = computed(() => hasAccessLevel("media_analyst"));

  const hasDERAccess = computed(() => hasAccessLevel("der"));

  const hasCopyrightAccess = computed(() =>
    hasAccessLevel("copyright_reporting")
  );

  const isInsightsAMTeam = computed(() => {
    const operationalTeam = currentUser.value.operational_team;

    if (!operationalTeam) return false;

    return operationalTeam.name.toLowerCase().includes("insights");
  });

  const canManageOrganisation = computed(() => {
    if (!hasOrganisationEditAccess.value) return false;

    if (hasSuperAccess.value || organisation.value?.status === "draft") {
      return true;
    }

    const allowedUserIds =
      organisation.value?.organisationsManagers.map(({ managerId }) =>
        Number(managerId)
      ) || [];

    const orgTeamId = organisation.value?.operationalTeamId;
    const operationalTeam = currentUser.value.operational_team;
    if (!operationalTeam) return false;

    if (orgTeamId && Number(orgTeamId) === operationalTeam.id) {
      return true;
    }

    return allowedUserIds.includes(currentUser.value.id);
  });

  async function logout(clearAll = false) {
    if (clearAll) {
      StorageService.remove("login");
    }

    await userLogout();

    currentUser.value.access_level = ["limited"];
  }

  async function getCurrentUser() {
    const response = await $streemApiAdmin.get("me", { handleErrors });
    currentUser.value = response.data;

    const [refreshToken, token] = await Promise.all([
      currentRefreshToken(),
      currentToken(),
    ]);

    if (token && currentUser.value.organisation?.id) {
      await updateUserToken(token, currentUser.value.organisation.id);
    }

    if (refreshToken && currentUser.value.organisation?.id) {
      await updateUserRefreshToken(
        refreshToken,
        currentUser.value.organisation.id
      );
    }
  }

  function getOrganisation(organisationId: number) {
    return Organisation.find(organisationId)
      .then((response) => {
        organisation.value = response.data;
      })
      .catch((error) => ErrorDialog("Failed to load organisation", error));
  }

  function setOrganisation(newOrganisation: Organisation): void {
    organisation.value = newOrganisation;
  }

  function unauthorized(error: unknown): void {
    loadError.value = error;
  }

  return {
    ...storeToRefs(sharedUserStore),
    accessLevels,
    adminRoles,
    canManageOrganisation,
    getCurrentUser,
    getOrganisation,

    hasAccessLevel,
    hasAIAccess,
    hasApiKeyAccess,
    hasCopyrightAccess,
    hasDERAccess,
    hasLimitedAccess,
    hasMediaAnalystAccess,
    hasNormalAccess,
    hasOrganisationEditAccess,
    hasOrganisationReadAccess,
    hasRosterAccess,
    hasSuperAccess,

    isInsightsAMTeam,

    loadError,
    loginFailed,
    logout,
    organisation,
    setOrganisation,
    unauthorized,
  };
});

export default useUserStore;
